import React, { useEffect, useRef, useState } from 'react';
import { ArrowClockwise } from '@phosphor-icons/react';

const THRESHOLD = 70;
const MAX_PULL = 110;

const isAtTop = () =>
  (window.scrollY || 0) + (document.querySelector('.app-shell')?.scrollTop || 0) <= 0;

export const PullToRefresh = ({ onRefresh, children }) => {
  const ref = useRef(null);
  const busy = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !onRefresh) return undefined;
    let startY = null;
    let dist = 0;

    const start = (e) => {
      dist = 0;
      startY = !busy.current && e.touches.length === 1 && isAtTop() ? e.touches[0].clientY : null;
    };
    const move = (e) => {
      if (startY === null) return;
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0 || !isAtTop()) { dist = 0; setPull(0); return; }
      dist = Math.min(dy * 0.5, MAX_PULL);
      setPull(dist);
      if (e.cancelable) e.preventDefault();
    };
    const end = async () => {
      if (startY === null) return;
      startY = null;
      if (dist < THRESHOLD) { setPull(0); return; }
      busy.current = true;
      setRefreshing(true);
      setPull(THRESHOLD);
      try { await onRefresh(); } finally {
        busy.current = false;
        setRefreshing(false);
        setPull(0);
      }
    };

    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, [onRefresh]);

  const ready = pull >= THRESHOLD;
  return (
    <div ref={ref} data-testid="pull-to-refresh">
      <div
        className="flex items-end justify-center overflow-hidden"
        style={{ height: pull, transition: startTransition(pull, refreshing) }}
        aria-hidden={pull === 0}
      >
        {pull > 0 && (
          <div
            className={`mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md ${ready ? 'text-indigo-600' : 'text-gray-400'}`}
            data-testid="pull-to-refresh-indicator"
          >
            <ArrowClockwise
              size={18}
              className={refreshing ? 'animate-spin' : ''}
              style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }}
            />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const startTransition = (pull, refreshing) => (pull === 0 || refreshing ? 'height 200ms ease-out' : 'none');

export default PullToRefresh;
